import { useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../types/RootState";

type History = ReturnType<typeof useHistory>;

/**
 * Return the user to where they came from (a history POP), falling back to a
 * canonical path when there is no in-app history (direct URL / bookmark /
 * reload on the current page). This is the same rule BackHistory uses for the
 * back arrow, kept here as the single source of truth so save/cancel handlers
 * behave identically to pressing the arrow (STIC-15615 R3).
 */
export const goBackOrTo = (
  history: History,
  historyCount: number,
  fallback: string
): void => {
  if (historyCount > 0) {
    history.goBack();
  } else {
    history.push(fallback);
  }
};

/**
 * Hook wrapper for form pages: returns a `goBack(fallback)` that pops to the
 * originating page, or pushes `fallback` (typically the entity list) when
 * there is no history to pop.
 */
export const useGoBackOrTo = () => {
  const history = useHistory();
  const historyCount = useSelector((state: RootState) => state.all.historyCount);

  return useCallback(
    (fallback: string) => goBackOrTo(history, historyCount, fallback),
    [history, historyCount]
  );
};

/**
 * Same pop as above, but stays on the current page when there is nothing to pop
 * instead of pushing a fallback — the rule BackHistory uses to hide the arrow
 * rather than invent a destination. For save handlers on pages that can be
 * opened straight from the sidebar (which resets the depth to 0, see
 * AppLayout's menu-root handling): there is no list they came from, so the only
 * honest answer is to stay put.
 */
export const useGoBackOrStay = () => {
  const history = useHistory();
  const historyCount = useSelector((state: RootState) => state.all.historyCount);

  return useCallback(() => {
    if (historyCount > 0) {
      history.goBack();
    }
  }, [history, historyCount]);
};
